'use client';

/**
 * Employee Portal — rich dashboard landing.
 *
 * Renders the same <PortalPreview> component that the admin sees in the
 * view-as impersonation preview (`app/(portal-preview)/user-portal/[employeeId]/page.tsx`),
 * but powered by portal-JWT-scoped hooks for the LOGGED-IN recipient instead
 * of admin-impersonated employeeId params.
 *
 * Regression that this fixes: NC only built the rich portal mockup inside
 * the admin builder (<PortalPreview>). The actual employee /portal route was
 * an HFv3-original 3-card MVP. This commit makes both routes render the same
 * <PortalPreview> component with real data.
 *
 * Data-fetch strategy (portal JWT via fetchPortalApi — no admin JWT required):
 *   - portalAuthApi.getRecipient()                  → identity (no round-trip)
 *   - usePublicPortalSettings(workspaceId)           → portal settings/theme
 *   - usePortalNewsFeed()                            → published news (portal JWT)
 *   - usePortalGamificationSettings() + usePortalBadgePacks() → badge pack + levels (portal JWT)
 *   - usePortalPointsHistory({ limit: 200 })         → points activity (portal JWT)
 *   - usePortalEarnedBadges()                        → earned badges (portal JWT)
 *   - usePortalLeaderboard({ scope, period, limit }) → leaderboard (portal JWT)
 *   - usePortalEnrollments()                         → training enrollments (portal JWT)
 *
 * The layout's <header> is retained (it provides nav links). PortalPreview is
 * mounted with inline={true} which suppresses its own internal header so the
 * two don't stack.
 */

import { Suspense, useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { PortalPreview } from '@/app/settings/portal/_components/portal-preview';
import { CelebrationProvider } from '@/app/(portal)/_components/celebrations/celebration-provider';
import { useCelebrationDetector } from '@/app/(portal)/_components/celebrations/celebration-detector';
import type { PortalUser } from '@/lib/user-portal/types';
import type { BadgeAwardEntry, LevelAwardEntry, PointsAwardEntry } from '@/lib/gamification/types';
import {
  transformNewsToProps,
  transformToTrainingSlides,
  transformToCourseCards,
  transformPointsHistory,
  transformToBadgeCards,
  // FIX #686: transformToCertificateCards removed — certificates now come
  // from usePortalCertificates (actual issued certs) not enrollment status.
  findActiveBadgePack,
  transformLevels,
} from '@/lib/user-portal/transform-portal-data';

// Portal-scoped hooks (portal JWT via fetchPortalApi)
import { usePublicPortalSettings } from '@/components/portal/hooks/use-portal-settings';
import { usePortalNewsFeed } from '@/components/portal/hooks/use-portal-news';
import {
  usePortalGamificationSettings,
  usePortalBadgePacks,
  usePortalPointsHistory,
  usePortalEarnedBadges,
  usePortalLeaderboard,
} from '@/components/portal/hooks/use-portal-gamification';
import { usePortalEnrollments, usePortalDashboard } from '@/components/portal/hooks/use-portal-training';
// FIX #686: import real certificate hook so Certificates tab reads actual
// issued certs (course_completions) rather than enrollment status.
import { usePortalCertificates } from '@/components/portal/hooks/use-portal-certificates';

// Identity — no round-trip needed; set at direct-login time
import { portalAuthApi } from '@/lib/api/portal-auth';
import { Skeleton } from '@/components/ui/skeleton';
import type { GamificationSettings as AdminGamSettings, PointTransaction } from '@/lib/types/gamification';
import type { GamificationSettings as LegacyGamSettings } from '@/lib/gamification/types';
import type { PortalEnrollment } from '@/components/portal/hooks/use-portal-training';

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

/**
 * Bridge AdminGamSettings → the minimal LegacyGamSettings shape that
 * findActiveBadgePack reads (only `activeBadgePackId`).
 * Admin backend uses `selectedBadgePackId`; legacy transformer expects
 * `activeBadgePackId`.
 */
function toFindPackSettings(s: AdminGamSettings | null | undefined): LegacyGamSettings | null {
  if (!s) return null;
  return { activeBadgePackId: s.selectedBadgePackId ?? null } as LegacyGamSettings;
}

// Map backend PointTransaction shape → legacy award-entry shapes
// used by transform-portal-data transformers.

function ptToPointsAward(pt: PointTransaction): PointsAwardEntry {
  const meta = (pt.metadata ?? {}) as Record<string, unknown>;
  return {
    id: pt.id,
    dateTime: pt.createdAt,
    points: pt.points,
    employeeName: String(meta.recipientName ?? ''),
    employeeEmail: String(meta.recipientEmail ?? ''),
    activity: pt.description || String(pt.eventType),
    moduleType: pt.sourceType,
    campaignId: meta.campaignId ? String(meta.campaignId) : undefined,
    campaignName: meta.campaignName ? String(meta.campaignName) : undefined,
    trainingType: meta.trainingType ? String(meta.trainingType) : undefined,
    trainingName: meta.trainingName ? String(meta.trainingName) : undefined,
  };
}

function ptToBadgeAward(pt: PointTransaction): BadgeAwardEntry {
  const meta = (pt.metadata ?? {}) as Record<string, unknown>;
  return {
    id: pt.id,
    dateTime: pt.createdAt,
    badgeName: String(meta.badgeName ?? ''),
    badgeIcon: String(meta.badgeIcon ?? ''),
    employeeName: String(meta.recipientName ?? ''),
    employeeEmail: String(meta.recipientEmail ?? ''),
    event: String(pt.eventType),
    eventCount: typeof meta.eventCount === 'number' ? meta.eventCount : 1,
  };
}

function ptToLevelAward(pt: PointTransaction): LevelAwardEntry {
  const meta = (pt.metadata ?? {}) as Record<string, unknown>;
  return {
    id: pt.id,
    dateTime: pt.createdAt,
    levelName: String(meta.levelName ?? ''),
    levelIcon: String(meta.levelIcon ?? ''),
    employeeName: String(meta.recipientName ?? ''),
    employeeEmail: String(meta.recipientEmail ?? ''),
    pointsRequired: typeof meta.pointsRequired === 'number' ? meta.pointsRequired : 0,
    previousLevel: String(meta.previousLevel ?? ''),
  };
}

/**
 * Coerce PortalEnrollment (portal-JWT endpoint shape) → EnrollmentLike
 * consumed by the transform-portal-data functions.
 *
 * The portal endpoint pre-scopes enrollments to the recipient, so
 * `learnerEmail` is always the logged-in user's email.
 */
function portalEnrollmentToLike(
  e: PortalEnrollment,
  recipientEmail: string,
) {
  return {
    // FIX #681/#682: pass enrollment id so transformers can surface enrollmentId
    // on TrainingSlideProp / CourseCardProp for player navigation.
    id: e.id,
    learnerEmail: recipientEmail,
    courseId: e.courseId,
    courseName: e.courseName,
    // FIX #670 #671 #672: preserve Title Case status from BE
    // ('Completed' | 'In Progress' | 'Overdue' | 'Not Started' | ...).
    // The previous .toUpperCase().replace(/\s/g, '_') converted them to
    // UPPER_SNAKE_CASE which broke every status comparison in portal-preview.tsx
    // (evalTrained, evalExpired, carousel filter all compare against Title Case).
    // Result: Trained=0, Expired/Overdue=0, Completed courses leaked into slides.
    status: e.status,
    progress: e.progress ?? e.progressPercent ?? 0,
    dueDate: e.dueDate ?? '',
    completionDate: e.completedAt ?? null,
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function PortalDashboardInner() {
  // Identity — set at direct-login time; no round-trip
  const recipient = useMemo(() => portalAuthApi.getRecipient(), []);
  const workspaceId = recipient?.workspaceId ?? '';
  const recipientEmail = recipient?.email ?? '';
  const recipientName = recipient
    ? `${recipient.firstName ?? ''} ${recipient.lastName ?? ''}`.trim() || recipientEmail
    : '';

  // ── Section routing from ?section= query param ───────────────────────────
  // Subroutes (/portal/badges, /portal/training, …) redirect here with a
  // ?section=<name> param. We read it once on mount to: (a) activate the
  // right bottom-strip tab via initialActivePortalTab, and (b) scroll to
  // the anchor element after PortalPreview renders.
  const searchParams = useSearchParams();
  const sectionParam = searchParams?.get('section') ?? '';

  // Map section name → PortalPreview tab index (0-4)
  const SECTION_TAB: Record<string, number> = {
    training: 0,
    trainings: 0,
    leaderboard: 1,
    points: 2,
    badges: 3,
    badge: 3,
    certificates: 4,
    certificate: 4,
    // #676 — `dashboard` is a no-op sentinel that lands the user on the
    // default portal view (scrolls to top, no tab override). Without this
    // entry, deep-links like /portal?section=dashboard would be treated
    // as "unknown section" and look like the param was silently ignored.
  };
  // Section names that scroll to an anchor instead of (or in addition to)
  // activating a tab.
  const SECTION_ANCHOR: Record<string, string> = {
    news: 'portal-section-news',
    training: 'portal-section-tabs',
    trainings: 'portal-section-tabs',
    leaderboard: 'portal-section-tabs',
    points: 'portal-section-tabs',
    badges: 'portal-section-tabs',
    badge: 'portal-section-tabs',
    certificates: 'portal-section-tabs',
    certificate: 'portal-section-tabs',
    // #676 — `dashboard` deliberately resolves to the page top (no anchor).
    dashboard: '',
  };

  const initialActivePortalTab = sectionParam ? SECTION_TAB[sectionParam] : undefined;

  useEffect(() => {
    if (!sectionParam) return;
    const anchorId = SECTION_ANCHOR[sectionParam];
    // #676 — `dashboard` sentinel resolves to empty anchor → scroll to top.
    // Unknown sections are silently ignored (debug-logged so we can spot drift).
    if (anchorId === undefined) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug(`[portal] unknown ?section=${sectionParam} — known: ${Object.keys(SECTION_ANCHOR).join(', ')}`);
      }
      return;
    }

    // FIX #784 (2026-05-25): the previous `setTimeout(150)` raced against
    // `<PortalPreview>` mount — on slow loads (suspense, async data fetch)
    // the anchor didn't exist in the DOM yet, `getElementById` returned
    // null, and the scroll silently no-op'd. Replaced with a bounded
    // requestAnimationFrame poll (~60 frames @ 16ms = ~1s window) that
    // mirrors the existing `scrollToTrainings` pattern in
    // `app/settings/portal/_components/portal-preview.tsx:402-414`.
    // The empty-anchor 'dashboard' sentinel still scrolls to top
    // immediately — no anchor to wait for.
    if (anchorId === '') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    let rafId: number | null = null;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // ~1 second @ 60fps — covers Suspense + first paint

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < MAX_ATTEMPTS) {
        rafId = window.requestAnimationFrame(tryScroll);
      } else if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug(`[portal] anchor #${anchorId} not found after ${MAX_ATTEMPTS} frames — giving up`);
      }
    };

    rafId = window.requestAnimationFrame(tryScroll);

    return () => {
      cancelled = true;
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionParam]);

  // UI state
  const [previewDarkMode, setPreviewDarkMode] = useState(false);
  const [isManagerView, setIsManagerView] = useState(false);
  const [managerViewTab, setManagerViewTab] = useState<'dashboard' | 'myteam'>('dashboard');

  // ── Data fetches ──────────────────────────────────────────────────────────

  // Portal-settings: public variant (no admin JWT needed; workspace-level)
  const { data: portalSettingsResolved, isLoading: loadingSettings } =
    usePublicPortalSettings(workspaceId);

  // News: portal-JWT scoped, server-side filters publish-window + targeting
  const { data: newsRawData, isLoading: loadingNews } = usePortalNewsFeed();

  // Gamification settings + badge packs: portal-JWT scoped (workspace-level config)
  const { data: gamSettings, isLoading: loadingGamSettings } = usePortalGamificationSettings();
  const { data: badgePacks, isLoading: loadingPacks } = usePortalBadgePacks();

  // Points activity: portal-JWT scoped, auto-filtered to this recipient
  const { data: pointsHistoryPage, isLoading: loadingPoints } =
    usePortalPointsHistory({ limit: 200 });

  // Earned badges: portal-JWT scoped
  const { data: earnedBadgesRaw, isLoading: loadingBadges } = usePortalEarnedBadges();

  // Leaderboard: portal-JWT scoped
  const { data: leaderboardData, isLoading: loadingLeaderboard } = usePortalLeaderboard({
    scope: 'Workspace',
    period: 'All Time',
    limit: 10,
  });

  // Enrollments: portal-JWT scoped, auto-filtered to this recipient
  const { data: enrollmentsRaw, isLoading: loadingEnrollments } = usePortalEnrollments();

  // FIX #686: real issued certificates from course_completions (portal JWT).
  // Previously certificateCards was built from enrollment status (always
  // "In Progress" for incomplete rows) instead of actual issuance events.
  // Now we load from GET /v1/portal/certificates which only returns rows that
  // have a course_completions record (i.e. genuinely issued certificates).
  const { data: issuedCertsRaw } = usePortalCertificates();

  // FIX #668 #673: fetch dashboard summary for real simulation stats + riskStatus.
  // This avoids the synthetic fallback (sent=3, riskLevel='Low') that was
  // hardcoded because portalUser.timesCompromised was never populated.
  const { data: portalDashboard } = usePortalDashboard();

  const loading =
    loadingSettings ||
    loadingNews ||
    loadingGamSettings ||
    loadingPacks ||
    loadingPoints ||
    loadingBadges ||
    loadingLeaderboard ||
    loadingEnrollments;

  // ── Derived data (memoised) ───────────────────────────────────────────────

  const portalSettings = portalSettingsResolved?.settings ?? null;

  // Sync dark-mode default from settings on first resolved value.
  useMemo(() => {
    if (portalSettings?.defaultEndUserTheme === 'Dark') {
      setPreviewDarkMode(true);
    }
    // Intentional single-dep memo — only apply settings-driven default once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalSettings?.defaultEndUserTheme]);

  // Points: ActivityLogPage rows → PointsAwardEntry[]
  const pointsAwards = useMemo<PointsAwardEntry[]>(
    () => (pointsHistoryPage?.rows ?? []).map(ptToPointsAward),
    [pointsHistoryPage],
  );

  // Badge awards: filter points log rows that are badge-event type
  const badgeAwards = useMemo<BadgeAwardEntry[]>(
    () =>
      (pointsHistoryPage?.rows ?? [])
        .filter((pt) => pt.sourceType === 'BADGE')
        .map(ptToBadgeAward),
    [pointsHistoryPage],
  );

  // Level awards: filter points log rows that are level-up event type
  const levelAwards = useMemo<LevelAwardEntry[]>(
    () =>
      (pointsHistoryPage?.rows ?? [])
        .filter((pt) => pt.sourceType === 'LEVEL')
        .map(ptToLevelAward),
    [pointsHistoryPage],
  );

  // Active badge pack + derived transformations
  const activeBadgePack = useMemo(
    () => findActiveBadgePack(badgePacks ?? [], toFindPackSettings(gamSettings)),
    [badgePacks, gamSettings],
  );
  const levelData = useMemo(() => transformLevels(activeBadgePack), [activeBadgePack]);

  // Earned badge IDs (as string[] for transformToBadgeCards)
  const earnedBadgeIds = useMemo<string[]>(
    () => (earnedBadgesRaw ?? []).map((eb) => eb.badgeId),
    [earnedBadgesRaw],
  );

  // Leaderboard entries
  const leaderboardEntries = leaderboardData?.entries ?? [];
  const thisEntry = leaderboardEntries.find((e) => e.email === recipientEmail);
  const totalPoints =
    thisEntry?.totalPoints ?? pointsAwards.reduce((sum, a) => sum + a.points, 0);
  const employeeRank = thisEntry?.rank ?? leaderboardEntries.length + 1;
  const badgeCount = thisEntry?.badgeCount ?? earnedBadgesRaw?.length ?? 0;
  const employeeBadges = thisEntry?.badges ?? [];

  // FIX #673: map the DB riskStatus enum (None/Safe/Low/Medium/High/Very High)
  // to the portal risk level ('Low'|'Medium'|'High') used by portal-preview
  // to render the Risk Score widget. Previously hardcoded to 'Low' → always 25.
  const derivedRiskLevel = useMemo<'Low' | 'Medium' | 'High'>(() => {
    const rs = portalDashboard?.riskStatus ?? '';
    if (rs === 'High' || rs === 'Very High') return 'High';
    if (rs === 'Medium') return 'Medium';
    return 'Low';
  }, [portalDashboard?.riskStatus]);

  // PortalUser shape — isManager is sourced from the portal-JWT claim that
  // the BE embeds at issuance (recipients.manager_id back-ref check).
  // Legacy tokens issued before that claim existed default to false; the
  // toggle stays hidden until the user re-logs.
  const portalUser = useMemo<PortalUser | null>(() => {
    if (!recipient) return null;
    // FIX #668: use real compromised count from simulation stats.
    const realCompromised = portalDashboard?.simulationStats?.totalCompromised ?? 0;
    // FIX #673: use real training completion rate from dashboard stats.
    const realTrainingCompletion = portalDashboard?.trainingStats?.completionRate ?? 0;
    return {
      id: recipient.id,
      name: recipientName,
      email: recipientEmail,
      upn: recipientEmail,
      department: '',
      designation: '',
      location: '',
      avatar: '',
      hasPortalAccess: true,
      totalPoints,
      badgeCount,
      badges: employeeBadges,
      rank: employeeRank,
      riskLevel: derivedRiskLevel,
      timesCompromised: realCompromised,
      trainingCompletion: realTrainingCompletion,
      reportRate: 0,
      isManager: recipient.isManager === true,
      directReports: 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipient, totalPoints, badgeCount, employeeRank, derivedRiskLevel, portalDashboard]);

  // Leaderboard prop (top 5) for PortalPreview
  const leaderboardProp = useMemo(
    () =>
      leaderboardEntries.slice(0, 5).map((e) => ({
        rank: e.rank,
        name: e.name,
        dept: e.department,
        points: e.totalPoints,
        isCurrentUser: e.email === recipientEmail,
      })),
    [leaderboardEntries, recipientEmail],
  );

  // News: usePortalNewsFeed returns PortalNews[] directly.
  // transformNewsToProps expects NewsUpdate[] (legacy NC shape). The fields
  // overlap: PortalNews has title, content (→ description), thumbnailUrl,
  // publishStart, publishEnd, isActive. Coerce inline.
  const newsItems = useMemo(() => {
    const rows = Array.isArray(newsRawData) ? newsRawData : [];
    if (rows.length === 0) return undefined;
    // Portal feed is already server-filtered to published+window+targeting,
    // so pass isActive=true for all rows to skip client-side filter in transformer.
    const asNewsUpdate = rows.map((n) => ({
      isActive: true,
      title: n.title ?? '',
      content: n.content ?? '',
      thumbnailUrl: n.thumbnailUrl ?? '',
      publishStart: n.publishStart ?? n.createdAt ?? new Date().toISOString(),
      publishEnd: n.publishEnd ?? new Date(Date.now() + 86400000 * 365).toISOString(),
      createdOn: n.createdAt ?? '',
    }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return transformNewsToProps(asNewsUpdate as any);
  }, [newsRawData]);

  // Enrollments: coerce PortalEnrollment → EnrollmentLike for transformers
  const enrollmentLikes = useMemo(
    () => (enrollmentsRaw ?? []).map((e) => portalEnrollmentToLike(e, recipientEmail)),
    [enrollmentsRaw, recipientEmail],
  );

  // Training slides (in-progress courses)
  const trainingSlides = useMemo(
    () =>
      enrollmentLikes.length > 0
        ? transformToTrainingSlides(enrollmentLikes, [], recipientEmail)
        : undefined,
    [enrollmentLikes, recipientEmail],
  );

  // Course cards
  const courseCards = useMemo(
    () =>
      enrollmentLikes.length > 0
        ? transformToCourseCards(enrollmentLikes, [], recipientEmail)
        : undefined,
    [enrollmentLikes, recipientEmail],
  );

  // FIX #686: build certificateCards from ACTUAL issued certs
  // (GET /v1/portal/certificates → course_completions rows) instead of
  // deriving status from enrollment.status which was always "In Progress"
  // for any course that hadn't yet generated a completion record.
  // Each PortalCertificate row only exists when a cert was genuinely issued;
  // its certificateIssuedAt is always set, so status is always "Completed".
  const certificateCards = useMemo(() => {
    const certs = issuedCertsRaw ?? [];
    if (certs.length === 0) return undefined;
    return certs.map((c) => ({
      name: `${c.courseName} Certificate`,
      date: c.certificateIssuedAt
        ? new Date(c.certificateIssuedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : c.completionDate
          ? new Date(c.completionDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : 'Pending',
      // All rows from course_completions are definitively issued — "Completed".
      status: 'Completed' as const,
    }));
  }, [issuedCertsRaw]);

  // Points history (this recipient only)
  const pointsHistory = useMemo(
    () =>
      pointsAwards.length > 0
        ? transformPointsHistory(pointsAwards, recipientName || undefined)
        : undefined,
    [pointsAwards, recipientName],
  );

  // Badge cards (requires activeBadgePack from portal-JWT usePortalBadgePacks)
  const badgeCards = useMemo(
    () =>
      activeBadgePack
        ? transformToBadgeCards(
            activeBadgePack,
            earnedBadgeIds,
            badgeAwards,
            pointsAwards,
            recipientEmail,
          )
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeBadgePack, badgeAwards, pointsAwards, recipientEmail],
  );

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return <PortalDashboardSkeleton />;
  }

  if (!portalUser) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-muted-foreground">Unable to load your portal. Please log in again.</p>
      </div>
    );
  }

  return (
    // The (portal) layout already sets its <main> to full-width on the
    // dashboard route + suppresses its header so <PortalPreview> can own
    // the chrome. We just render fullscreen here. inline={true} keeps
    // PortalPreview from rendering its admin-style back-button strip.
    <div className="w-full">
      <CelebrationProvider employeeId={portalUser.id}>
        <CelebrationRunner
          user={portalUser}
          pointsAwards={pointsAwards}
          badgeAwards={badgeAwards}
          levelAwards={levelAwards}
        />
        <PortalPreview
          inline
          selectedTheme={portalSettings?.selectedTheme ?? 'modern-teal'}
          previewDarkMode={previewDarkMode}
          onPreviewDarkModeToggle={() => {
            // FIX #684: On the real employee portal (inline=true) the toggle must
            // update <html>.class so next-themes CSS vars take effect globally.
            // The preview component only flips its own container's "dark" class —
            // that was fine in the admin preview iframe but not for the full-page
            // portal. We do it here (in the live-portal callsite) rather than in
            // the shared PortalPreview component so we don't break the admin
            // preview which intentionally scopes dark-mode to its container only.
            setPreviewDarkMode((v) => {
              const next = !v;
              if (typeof document !== 'undefined') {
                document.documentElement.classList.toggle('dark', next);
              }
              return next;
            });
          }}
          onClose={() => undefined}
          portalLogoLight={portalSettings?.portalLogoLight ?? null}
          portalLogoDark={portalSettings?.portalLogoDark ?? null}
          heroHeading={portalSettings?.heroHeading ?? 'Welcome to Security Portal'}
          heroDescription={
            portalSettings?.heroDescription ??
            'Stay informed and protected with our security awareness platform.'
          }
          heroImage={portalSettings?.heroImage ?? null}
          ctaButtonText={portalSettings?.ctaButtonText ?? 'EXPLORE COURSES'}
          coverImage={portalSettings?.coverImage ?? null}
          showStatsBar={portalSettings?.showStatsBar !== false}
          showImportantUpdates={portalSettings?.showImportantUpdates !== false}
          showAssignedTraining={portalSettings?.showAssignedTraining !== false}
          showNewsUpdates={portalSettings?.showNewsUpdates !== false}
          showPolicies={portalSettings?.showPolicies !== false}
          showProgressSection={portalSettings?.showProgressSection !== false}
          showYourRank={portalSettings?.showYourRank !== false}
          showPoints={portalSettings?.showPoints !== false}
          showBadges={portalSettings?.showBadges !== false}
          showCertificates={portalSettings?.showCertificates !== false}
          showRiskScore={portalSettings?.showRiskScore !== false}
          showCompromised={portalSettings?.showCompromised !== false}
          showAcknowledged={portalSettings?.showAcknowledged !== false}
          showEvaluated={portalSettings?.showEvaluated !== false}
          showTrainings={portalSettings?.showTrainings !== false}
          showLeaderboard={portalSettings?.showLeaderboard !== false}
          showPointsTab={portalSettings?.showPointsTab !== false}
          showBadgesTab={portalSettings?.showBadgesTab !== false}
          showCertificatesTab={portalSettings?.showCertificatesTab !== false}
          isManagerView={isManagerView}
          onManagerViewChange={setIsManagerView}
          managerViewTab={managerViewTab}
          onManagerViewTabChange={setManagerViewTab}
          // Employee-specific identity
          employeeName={portalUser.name}
          employeeAvatar={portalUser.avatar}
          employeeEmail={portalUser.email}
          employeeDepartment={portalUser.department}
          employeeRank={portalUser.rank}
          employeeTotalPoints={portalUser.totalPoints}
          employeeBadgeCount={portalUser.badgeCount}
          employeeBadges={portalUser.badges}
          employeeRiskLevel={portalUser.riskLevel}
          employeeTimesCompromised={portalUser.timesCompromised}
          employeeTrainingCompletion={portalUser.trainingCompletion}
          employeeReportRate={portalUser.reportRate}
          isManager={portalUser.isManager}
          leaderboardData={leaderboardProp}
          // FIX #668: pass real phishing simulation stats so portal-preview renders
          // actual Sent/Clicked/Compromised counts instead of the synthetic fallback
          // (sent = Math.max(timesCompromised*3, 3) = 3 when timesCompromised=0).
          phishingStats={
            portalDashboard?.simulationStats
              ? {
                  sent: portalDashboard.simulationStats.totalReceived,
                  opened: 0, // not tracked at recipient level — omit for now
                  clicked: portalDashboard.simulationStats.totalClicked,
                  compromised: portalDashboard.simulationStats.totalCompromised,
                  reported: portalDashboard.simulationStats.totalReported,
                  trained: portalDashboard.trainingStats?.totalCompleted ?? 0,
                  evaluated: portalDashboard.trainingStats?.totalCompleted ?? 0,
                  campaignCount: portalDashboard.simulationStats.totalReceived,
                  lastIncident: null,
                }
              : null
          }
          // Real data from portal-JWT hooks
          newsItems={newsItems}
          trainingSlides={trainingSlides}
          courseCards={courseCards}
          pointsHistory={pointsHistory}
          badgeCards={badgeCards}
          certificateCards={certificateCards}
          activeBadgePack={activeBadgePack}
          levelData={levelData}
          initialActivePortalTab={initialActivePortalTab}
        />
      </CelebrationProvider>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Celebration detector (must live inside CelebrationProvider)
// ---------------------------------------------------------------------------

function CelebrationRunner({
  user,
  pointsAwards,
  badgeAwards,
  levelAwards,
}: {
  user: PortalUser;
  pointsAwards: PointsAwardEntry[];
  badgeAwards: BadgeAwardEntry[];
  levelAwards: LevelAwardEntry[];
}) {
  useCelebrationDetector({ user, pointsAwards, badgeAwards, levelAwards });
  return null;
}

// ---------------------------------------------------------------------------
// Admin redirect gate (audit P1-1, 2026-05-06)
//
// The /portal route is the EMPLOYEE-facing dashboard — every data hook in
// PortalDashboardInner uses fetchPortalApi which carries the portal JWT
// (`hf-portal:token`). When an admin lands here directly (typed URL or stale
// bookmark) they have an admin JWT but no portal JWT, so all 14 GETs to
// /v1/portal/* (dashboard, news, leaderboard, badges, gamification-settings,
// points/history, badges/earned, enrollments, etc.) 401 and the page renders
// "Unable to load your portal. Please log in again."
//
// The admin's actual intent is the in-app portal preview, which already
// exists at /settings/portal?tab=view-as (renders <PortalPreview> with
// admin-side hooks — no portal JWT required). Bounce admins there BEFORE
// PortalDashboardInner mounts so its hooks never fire. Employees with a real
// portal token continue to the dashboard untouched.
// ---------------------------------------------------------------------------
function AdminRedirectGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  // Tri-state: 'unknown' (not yet checked, SSR/first paint), 'allow' (employee),
  // 'bouncing' (admin — kick to settings/portal?tab=view-as).
  const [decision, setDecision] = useState<'unknown' | 'allow' | 'bouncing'>(
    'unknown',
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const adminToken = window.localStorage.getItem('hf-app:access-token');
    const portalToken = portalAuthApi.getToken();
    if (adminToken && !portalToken) {
      setDecision('bouncing');
      router.replace('/settings/portal?tab=view-as');
      return;
    }
    setDecision('allow');
  }, [router]);

  if (decision !== 'allow') {
    // 'unknown' (briefly during hydration) and 'bouncing' (admin — replacing
    // the route) both render the same neutral spinner. Crucially we DO NOT
    // render PortalDashboardInner here, which would mount the 14 portal-JWT
    // hooks and 401-storm before the redirect lands.
    return (
      <div
        className="flex items-center justify-center py-24"
        role="status"
        aria-live="polite"
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60" />
        <span className="sr-only">Loading portal…</span>
      </div>
    );
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Suspense wrapper — required by Next.js because PortalDashboardInner uses
// useSearchParams() during SSG. Without this, the build fails on prerender.
// ---------------------------------------------------------------------------
export default function PortalDashboard() {
  return (
    <Suspense
      fallback={
        <div
          className="flex items-center justify-center py-24"
          role="status"
          aria-live="polite"
        >
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60" />
          <span className="sr-only">Loading portal…</span>
        </div>
      }
    >
      <AdminRedirectGate>
        <PortalDashboardInner />
      </AdminRedirectGate>
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Portal dashboard skeleton — matches the page's section structure so the
// transition to real content is calm (no layout shift). Used during the
// initial multi-query fetch (settings + news + gamification + enrollments).
// ---------------------------------------------------------------------------
function PortalDashboardSkeleton() {
  return (
    <div className="w-full">
      {/* Portal chrome row (logo + toggle + user) */}
      <div className="sticky top-0 z-40 h-16 border-b border-border bg-card flex items-center justify-between px-8">
        <Skeleton className="h-8 w-40" />
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="space-y-1 hidden md:block">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-9 w-9 rounded-full" />
        </div>
      </div>
      <div className="w-full flex justify-center bg-background">
        <div className="w-full max-w-6xl px-8 py-10 space-y-10">
          {/* Hero card */}
          <div className="rounded-2xl border bg-muted/30 p-10 space-y-4">
            <Skeleton className="h-8 w-2/3 max-w-md" />
            <Skeleton className="h-4 w-1/2 max-w-sm" />
            <Skeleton className="h-10 w-40 rounded-md mt-4" />
          </div>
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border bg-card p-5 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
          {/* Tabs row */}
          <div className="flex gap-2">
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
          </div>
          {/* Training grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border bg-card overflow-hidden">
                <Skeleton className="h-40 w-full" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
